// Configuration for different modality types and their metrics
export const MODALITY_CONFIGS = {
  MRI: {
    // Manufacturer-specific configurations
    manufacturers: {
      GE: {
        // GE MRI specific metrics
        metrics: [
          { key: 'heliumLevel', label: 'Helium Level', unit: '%', min: 0, max: 100 },
          { key: 'heliumPressure', label: 'Helium Pressure', unit: 'psi', min: 0, max: 2 },
          { key: 'roomTemp', label: 'Room Temp', unit: '°F', min: 50, max: 80 },
          { key: 'roomHumidity', label: 'Room Humidity', unit: '%', min: 0, max: 100 },
          { key: 'waterFlow', label: 'Water Flow', unit: 'L/min', min: 0, max: 10 },
          { key: 'waterTemp', label: 'Water Temp', unit: '°F', min: 50, max: 80 },
          { key: 'compressor', label: 'Compressor Status', unit: '', min: 0, max: 1 },
        ],
        chartTypes: ['gauge', 'line'],
        defaultTimeRange: '-7d'
      },
      Siemens: {
        // Siemens Bluebox specific metrics
        metrics: [
          { key: 'heliumLevel', label: 'Helium Level', unit: '%', min: 0, max: 100 },
          { key: 'magnetPressure', label: 'Magnet Pressure', unit: 'psi', min: 0, max: 20 },
          { key: 'coldHeadTemp1', label: 'Cold Head Temperature', unit: '°C', min: 0, max: 100 },
          { key: 'shieldTemp1', label: 'Shield Temperature 1', unit: '°C', min: 0, max: 100 },
          { key: 'shieldTemp2', label: 'Shield Temperature 2', unit: '°C', min: 0, max: 100 },
          { key: 'turretTemp1', label: 'Turret Temperature 1', unit: '°C', min: 0, max: 100 },
          { key: 'turretTemp2', label: 'Turret Temperature 2', unit: '°C', min: 0, max: 100 },
          { key: 'roomTemp', label: 'Room Temperature', unit: '°F', min: 50, max: 80 },
          { key: 'roomHumidity', label: 'Room Humidity', unit: '%', min: 0, max: 100 },
          { key: 'waterFlow', label: 'Water Flow', unit: 'L/min', min: 0, max: 10 },
          { key: 'waterTemp', label: 'Water Temperature', unit: '°F', min: 50, max: 80 },
          { key: 'powerReading', label: 'Power Reading', unit: 'A', min: 0, max: 200 },
          { key: 'compressor', label: 'Compressor Status', unit: '', min: 0, max: 1 },
        ],
        chartTypes: ['gauge', 'line'],
        defaultTimeRange: '-7d'
      }
    },
    // Default fallback configuration (used for unknown manufacturers)
    default: {
      metrics: [
        { key: 'heliumLevel', label: 'Helium Level', unit: '%', min: 0, max: 100 },
        { key: 'heliumPressure', label: 'Helium Pressure', unit: 'psi', min: 0, max: 2 },
        { key: 'roomTemp', label: 'Room Temp', unit: '°F', min: 50, max: 80 },
        { key: 'roomHumidity', label: 'Room Humidity', unit: '%', min: 0, max: 100 },
        { key: 'waterFlow', label: 'Water Flow', unit: 'L/min', min: 0, max: 10 },
        { key: 'waterTemp', label: 'Water Temp', unit: '°F', min: 50, max: 80 },
        { key: 'compressor', label: 'Compressor Status', unit: '', min: 0, max: 1 },
      ],
      chartTypes: ['gauge', 'line'],
      defaultTimeRange: '-7d'
    }
  },
  CT: {
    // Default CT configuration (used for all manufacturers)
    default: {
      metrics: [
        { key: 'tubeScanSeconds', label: 'Tube Scan Seconds', unit: 'secs', min: 0, max: 1000000 },
        { key: 'mas', label: 'mAs', unit: 'mAs', min: 0, max: 100000 },
      ],
      chartTypes: ['gauge', 'line'],
      defaultTimeRange: '-30d'
    }
  },
  PETCT: {
    // Default PETCT configuration (used for all manufacturers)
    default: {
      metrics: [
        { key: 'tubeScanSeconds', label: 'Tube Scan Seconds', unit: 'secs', min: 0, max: 1000000 },
        { key: 'mas', label: 'mAs', unit: 'mAs', min: 0, max: 100000 },
      ],
      chartTypes: ['gauge', 'line'],
      defaultTimeRange: '-30d'
    }
  },
  PATIENT_MONITOR: {
    // Default Patient Monitor configuration (used for all manufacturers)
    default: {
      metrics: [
        { key: 'admitStatus', label: 'Admit Status', unit: '', min: 0, max: 1 },
      ],
      chartTypes: ['gauge'],
      defaultTimeRange: '-1d'
    }
  }
}

// Helper function to get configuration for a modality type and manufacturer
export const getModalityConfig = (modalityType, manufacturer = null) => {
  const baseConfig = MODALITY_CONFIGS[modalityType] || MODALITY_CONFIGS.MRI // Default to MRI if unknown
  
  // If manufacturer is specified and has configuration, use it
  if (manufacturer && baseConfig.manufacturers && baseConfig.manufacturers[manufacturer]) {
    return baseConfig.manufacturers[manufacturer]
  }
  
  // If manufacturer is not specified or not found, use default fallback
  if (baseConfig.default) {
    return baseConfig.default
  }
  
  // Fallback to base config for backward compatibility
  return baseConfig
}

// Helper function to check if a modality type supports a specific chart type
export const supportsChartType = (modalityType, chartType, manufacturer = null) => {
  const config = getModalityConfig(modalityType, manufacturer)
  return config.chartTypes.includes(chartType)
}

// Helper function to get metrics for a modality type and manufacturer
export const getModalityMetrics = (modalityType, manufacturer = null) => {
  const config = getModalityConfig(modalityType, manufacturer)
  return config.metrics
}
